import { useState, useEffect, useCallback } from 'react';
import type { RoleConfig, UserRole, Permission, PermissionValue } from '../types';
import {
  fetchRoles,
  getRoleById,
  getUserPermissions,
  getRolesForSelect,
  getRoleDisplayConfig,
  clearRoleCache,
} from '../services/roleService';

/**
 * Hook to fetch and manage all roles
 */
export function useRoles() {
  const [roles, setRoles] = useState<RoleConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadRoles = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const fetchedRoles = await fetchRoles(forceRefresh);
      setRoles(fetchedRoles);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch roles'));
      console.error('Error loading roles:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const refresh = useCallback(() => {
    return loadRoles(true);
  }, [loadRoles]);

  const clearCache = useCallback(() => {
    clearRoleCache();
    return loadRoles(true);
  }, [loadRoles]);

  return {
    roles,
    loading,
    error,
    refresh,
    clearCache,
  };
}

/**
 * Hook to get a specific role by ID
 */
export function useRole(roleId: UserRole | undefined) {
  const [role, setRole] = useState<RoleConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!roleId) {
      setRole(null);
      setLoading(false);
      return;
    }

    const loadRole = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedRole = await getRoleById(roleId);
        setRole(fetchedRole);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch role'));
        console.error('Error loading role:', err);
      } finally {
        setLoading(false);
      }
    };

    loadRole();
  }, [roleId]);

  return { role, loading, error };
}

/**
 * Hook to get permissions for a user based on their roles
 */
export function usePermissions(userRoles: UserRole[] | undefined) {
  const [permissions, setPermissions] = useState<PermissionValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userRoles || userRoles.length === 0) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    const loadPermissions = async () => {
      try {
        setLoading(true);
        setError(null);
        const userPermissions = await getUserPermissions(userRoles);
        setPermissions(userPermissions);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch permissions'));
        console.error('Error loading permissions:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, [userRoles]);

  const hasPermission = useCallback(
    (permission: PermissionValue) => {
      return permissions.includes(permission);
    },
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (requiredPermissions: PermissionValue[]) => {
      return requiredPermissions.some(permission => permissions.includes(permission));
    },
    [permissions]
  );

  const hasAllPermissions = useCallback(
    (requiredPermissions: PermissionValue[]) => {
      return requiredPermissions.every(permission => permissions.includes(permission));
    },
    [permissions]
  );

  return {
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
}

/**
 * Hook to get roles formatted for select/dropdown components
 */
export function useRoleOptions() {
  const [options, setOptions] = useState<Array<{ value: UserRole; label: string; description: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const roleOptions = await getRolesForSelect();
        setOptions(roleOptions);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch role options'));
        console.error('Error loading role options:', err);
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  return { options, loading, error };
}

/**
 * Hook to get display configuration for a specific role
 */
export function useRoleDisplay(roleId: UserRole | undefined) {
  const [display, setDisplay] = useState<{ label: string; variant: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!roleId) {
      setDisplay(null);
      setLoading(false);
      return;
    }

    const loadDisplay = async () => {
      try {
        setLoading(true);
        const config = await getRoleDisplayConfig(roleId);
        setDisplay(config);
      } catch (err) {
        console.error('Error loading role display config:', err);
        setDisplay(null);
      } finally {
        setLoading(false);
      }
    };

    loadDisplay();
  }, [roleId]);

  return { display, loading };
}
